/* eslint-disable quote-props */
import { rawTimeZones } from '@vvo/tzdb';

import { cmp } from '@/services/sorting';

export function suffixForDay(day) {
    let suffix = 'th';
    if (day === 1 || day === 21 || day === 31) {
        suffix = 'st';
    } else if (day === 2 || day === 22) {
        suffix = 'nd';
    } else if (day === 3 || day === 23) {
        suffix = 'rd';
    }
    return suffix;
}

const daysGenerator = (length) => {
    const days = [];
    for (let i = 1; i <= length; i++) {
        const suffix = ''; // suffixForDay(i);
        if (i <= 9) days.push({ label: `${i}${suffix}`, value: `0${i}` });
        else days.push({ label: `${i}${suffix}`, value: `${i}` });
    }
    return days;
};

export const DaysOfWeek = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
];

export const Days = {
    '01': daysGenerator(31),
    '02': daysGenerator(29),
    '03': daysGenerator(31),
    '04': daysGenerator(30),
    '05': daysGenerator(31),
    '06': daysGenerator(30),
    '07': daysGenerator(31),
    '08': daysGenerator(31),
    '09': daysGenerator(30),
    '10': daysGenerator(31),
    '11': daysGenerator(30),
    '12': daysGenerator(31),
};

export const Months = [
    { label: 'January', value: '01' },
    { label: 'February', value: '02' },
    { label: 'March', value: '03' },
    { label: 'April', value: '04' },
    { label: 'May', value: '05' },
    { label: 'June', value: '06' },
    { label: 'July', value: '07' },
    { label: 'August', value: '08' },
    { label: 'September', value: '09' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
];

export function zeroPadValue(base, targetLength) {
    if (!targetLength) { return null; }
    const baseStr = base.toString();
    const zeroesNeeded = (targetLength + 1) - baseStr.length;
    const padding = new Array(zeroesNeeded);
    padding.fill('0', 1, zeroesNeeded + 1);

    return padding.join('') + baseStr;
}

function formatTimezoneOffset(offset) {
    // "The number of minutes returned by getTimezoneOffset() is positive if the local time zone is
    // behind UTC, and negative if the local time zone is ahead of UTC. For example, for UTC+10,
    // -600 will be returned."
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset
    const usePositivePrefix = offset < 0;
    const originalNumericPortion = Math.abs(offset);
    const numericHours = Math.floor(originalNumericPortion / 60);
    const numericMinutes = originalNumericPortion - (numericHours * 60);
    const strHours = zeroPadValue(numericHours, 2);
    const strMinutes = zeroPadValue(numericMinutes, 2);

    const components = [usePositivePrefix ? '+' : '-', strHours, ':', strMinutes];

    return components.join('');
}

export const timezones = rawTimeZones
    .map<[string, number]>((tz) => ([tz.name, tz.rawOffsetInMinutes]));

export const timezoneOptions = rawTimeZones
    .filter((tz) => tz.rawOffsetInMinutes !== 0)
    .map((tz, index) => ({
        label: `${tz.name} (${formatTimezoneOffset(-tz.rawOffsetInMinutes)})`,
        value: index,
    }))
    .sort((a, b) => (cmp(a.label, b.label)));

const generateHourOptions = () => {
    const options = [];
    for (let i = 0; i < 24; i++) {
        let label = i % 12;
        if (label === 0) {
            label = 12;
        }
        options.push({
            value: i,
            label: `${label}:00${i > 11 ? 'pm' : 'am'}`,
        });
    }
    return options;
};

export const hourOptions = generateHourOptions();

const generateYearOptions = () => {
    const options = [];
    const currentYear = Number(new Date().getUTCFullYear());

    // create last 120 years (FUN FACT: oldest person in US current is 116 years old)

    for (let x = currentYear; x > currentYear - 120; x--) {
        const yearObj = { label: `${x}`, value: `${x}` };
        options.push(yearObj);
    }

    return options;
};

export const Years = generateYearOptions();

export function timezoneIndexFor(name) {
    if (name === '' || !name) {
        return -1;
    }
    return timezones.findIndex((tz) => tz[0] === name);
}

export function getLocalTimezone() {
    let localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (localTZ === 'UTC') {
        localTZ = 'England/London';
    }

    const TZ = timezoneOptions
        .findIndex((timezoneOption) => timezoneOption.label.includes(localTZ));

    let val = 0;
    if (TZ >= 0) {
        val = timezoneOptions[TZ].value;
    }

    return val;
}

export function formatTimeframe(user, record) {
    const pieces = [];

    let hourPiece = '';
    if (record.hour >= 12) {
        if (record.hour === 12) {
            hourPiece = 'Noon';
        } else {
            hourPiece = `${record.hour - 12} PM`;
        }
    } else if (record.hour === 0) {
        hourPiece = 'Midnight';
    } else {
        hourPiece = `${record.hour} AM`;
    }
    if (record.timezoneName) {
        hourPiece = `${hourPiece} ${record.timezoneName},`;
    } else if (user?.timezoneName) {
        hourPiece = `${hourPiece} ${user.timezoneName},`;
    } else {
        hourPiece = `${hourPiece} UTC,`;
    }
    pieces.push(hourPiece);

    if (!!record.dayOfTheWeek || record.dayOfTheWeek === 0) {
        const dayIdx = DaysOfWeek.findIndex((day) => day.value === record.dayOfTheWeek);
        const dayName = DaysOfWeek[dayIdx].label;
        pieces.push('on');
        pieces.push(`${dayName}s`);
    } else if (record.dayOfTheMonth) {
        pieces.push('on the');
        const suffix = suffixForDay(record.dayOfTheMonth);
        pieces.push(`${record.dayOfTheMonth}${suffix}`);
    } else {
        pieces.push('daily');
    }

    if (record.month) {
        const monthStr = zeroPadValue(record.month, 2);
        const monthIdx = Months.findIndex((month) => month.value === monthStr);
        const month = Months[monthIdx].label;
        if (record.dayOfTheMonth) {
            pieces.push(`of ${month}`);
        } else {
            pieces.push(`in ${month}`);
        }
    } else if (record.dayOfTheMonth) {
        pieces.push('of every month');
    }

    return pieces.join(' ');
}

export function getDateParts(isoDate, padded = true) {
    if (!isoDate || typeof isoDate !== 'string') {
        return {
            year: null,
            month: null,
            day: null,
        };
    }
    const date = new Date(isoDate);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1; // Adding 1 since getMonth() returns zero-based index
    const day = date.getUTCDate();

    if (padded) {
        return {
            year: `${year}`.padStart(4, '0'),
            month: `${month}`.padStart(2, '0'),
            day: `${day}`.padStart(2, '0'),
        };
    }

    return { year, month, day };
}
